import { Injectable } from '@angular/core';
import { INameValue } from 'src/app/common/interfaces/name-value-interface';
import {
  ITableServerExtraFilter,
  ITableServerFilter,
} from 'src/app/common/interfaces/table/table-server-filter.inteface';
import { ITableServerResponseFilter } from 'src/app/common/interfaces/table/table-server-filter-response.interface';
import { AbstractRejectAnalysisService } from '../abstraction/abstract.reject-analysis.service';
import { IRejectAnalysisTransactionDetail } from '../../interfaces/reject-analysis/reject-analysis-transaction-detail.interface';
import { IRejectAnalysisTransactionDetailsRequest } from '../../interfaces/reject-analysis/reject-analysis-transaction-details-request.interface';
import { AbstractTransactionService } from '../abstraction/abstract.transaction.service';

interface IRejectAnalysisDetailRow {
  code: string;
  description: string;
  totalCount: number;
  totalAmount: number;
}

interface IRejectAnalysisMockRow {
  participationType: string;
  activityDate: string;
  totalCtReceived: number;
  details: IRejectAnalysisDetailRow[];
}

const MOCK_ROWS: IRejectAnalysisMockRow[] = [
  {
    participationType: 'Receive Only',
    activityDate: '2026-06-30',
    totalCtReceived: 791,
    details: [
      { code: 'AB05', description: 'Core time out', totalCount: 12, totalAmount: 135788.52 },
      { code: 'AC01', description: 'Invalid account', totalCount: 84, totalAmount: 921000.11 },
      { code: 'AC04', description: 'Closed account', totalCount: 602, totalAmount: 4289910.35 },
    ],
  },
  {
    participationType: 'Send and Receive',
    activityDate: '2026-06-29',
    totalCtReceived: 925,
    details: [
      { code: 'AB05', description: 'Core time out', totalCount: 34, totalAmount: 445111.08 },
      { code: 'AC01', description: 'Invalid account', totalCount: 25, totalAmount: 168932.19 },
      { code: 'AM04', description: 'Insufficient funds', totalCount: 51, totalAmount: 709110.44 },
    ],
  },
  {
    participationType: 'Receive Only',
    activityDate: '2026-06-27',
    totalCtReceived: 640,
    details: [
      {
        code: 'AG01',
        description: 'Invalid transaction type',
        totalCount: 11,
        totalAmount: 58002.6,
      },
      { code: 'AB08', description: 'Connection issue', totalCount: 17, totalAmount: 103456.07 },
    ],
  },
];

@Injectable({
  providedIn: 'root',
})
export class MockRejectAnalysisService extends AbstractRejectAnalysisService {
  private loadedRejectCodeOptions: INameValue[] = [];

  constructor(private transactionService: AbstractTransactionService) {
    super();
  }

  private loadRows(): IRejectAnalysisMockRow[] {
    return MOCK_ROWS;
  }

  async tableFilter(filter: ITableServerFilter): Promise<ITableServerResponseFilter | undefined> {
    const allRows = this.loadRows();

    const startDate = new Date(filter.startDate);
    const endDate = new Date(filter.endDate);
    startDate.setHours(0, 0, 0, 0);
    endDate.setHours(23, 59, 59, 999);

    const selectedCode =
      filter.extendedData?.find((x: ITableServerExtraFilter) => x.key === 'RejectCode')?.value ??
      'All';
    const searchText = (filter.value ?? '').trim().toLowerCase();

    const summaryRows: any[] = [];
    const uniqueRejectCodes = new Map<string, string>();

    for (const row of allRows) {
      const rowDate = new Date(row.activityDate);
      rowDate.setHours(12, 0, 0, 0);
      if (rowDate < startDate || rowDate > endDate) {
        continue;
      }

      const matchedDetails = row.details.filter(detail => {
        const codeMatch = !selectedCode || selectedCode === 'All' || detail.code === selectedCode;
        if (!codeMatch) {
          return false;
        }
        if (!searchText) {
          return true;
        }
        return (
          `${detail.code} ${detail.description}`.toLowerCase().includes(searchText) ||
          row.participationType.toLowerCase().includes(searchText)
        );
      });

      if (matchedDetails.length === 0) {
        continue;
      }

      matchedDetails.forEach(detail => {
        if (!uniqueRejectCodes.has(detail.code)) {
          uniqueRejectCodes.set(detail.code, detail.description);
        }
      });

      const receiveCtRejected = matchedDetails.reduce((sum, d) => sum + d.totalCount, 0);
      const receiverRejectionPct =
        row.totalCtReceived > 0
          ? `${Number(((receiveCtRejected / row.totalCtReceived) * 100).toFixed(2))}%`
          : '0.00%';

      summaryRows.push({
        participationType: row.participationType,
        activityDate: row.activityDate,
        totalCtReceived: row.totalCtReceived,
        receiveCtRejected,
        receiverRejectionPct,
        dataKey: `${row.participationType}|${row.activityDate}`,
      });
    }

    if (filter.sortBy) {
      const sortField = filter.sortBy.charAt(0).toLowerCase() + filter.sortBy.slice(1);
      summaryRows.sort((a, b) => {
        const av = a[sortField] ?? '';
        const bv = b[sortField] ?? '';
        const cmp = String(av).localeCompare(String(bv), undefined, { numeric: true });
        return filter.sortAsc ? cmp : -cmp;
      });
    }

    const pageSize = filter.pageSize ?? 10;
    const currentPage = filter.currentPage ?? 1;
    const start = (currentPage - 1) * pageSize;

    this.loadedRejectCodeOptions = [
      { name: 'All', value: 'All' },
      ...Array.from(uniqueRejectCodes.entries())
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([code, description]) => ({ name: code, value: `${code} - ${description}` })),
    ];

    return {
      currentPage,
      pageCount: Math.ceil(summaryRows.length / pageSize),
      pageSize,
      rowCount: summaryRows.length,
      queryable: summaryRows.slice(start, start + pageSize),
    };
  }

  getLoadedRejectCodeOptions(): INameValue[] {
    return this.loadedRejectCodeOptions;
  }

  async getDetailsForRow(dataKey: string): Promise<any[]> {
    const allRows = this.loadRows();
    const separatorIndex = dataKey.indexOf('|');
    const participationType = dataKey.substring(0, separatorIndex);
    const activityDate = dataKey.substring(separatorIndex + 1);

    const row = allRows.find(
      r => r.participationType === participationType && r.activityDate === activityDate
    );

    if (!row) {
      return [];
    }

    const totalRejects = row.details.reduce((sum, d) => sum + d.totalCount, 0);

    return row.details.map(detail => ({
      rejectCode: `${detail.code} - ${detail.description}`,
      totalCount: detail.totalCount,
      totalAmount: detail.totalAmount,
      totalRejectPercent:
        totalRejects > 0
          ? `${Number(((detail.totalCount / totalRejects) * 100).toFixed(2))}%`
          : '0.00%',
      dataKey,
    }));
  }

  async getRejectCodeOptions(): Promise<INameValue[]> {
    const allRows = this.loadRows();
    const uniqueCodeMap = new Map<string, string>();

    allRows.forEach(row => {
      row.details.forEach(detail => {
        if (!uniqueCodeMap.has(detail.code)) {
          uniqueCodeMap.set(detail.code, detail.description);
        }
      });
    });

    const options: INameValue[] = [{ name: 'All', value: 'All' }];
    Array.from(uniqueCodeMap.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .forEach(([code, description]) => {
        options.push({ name: code, value: `${code} - ${description}` });
      });

    return options;
  }

  async getRespondentOptions(): Promise<INameValue[]> {
    return [
      { name: 'All', value: 'All' },
      { name: '021000021', value: '021000021 - First National Bank' },
      { name: '031100209', value: '031100209 - Community Credit Union' },
      { name: '044000037', value: '044000037 - Regional Savings Bank' },
    ];
  }

  async getTransactionDetails(
    request: IRejectAnalysisTransactionDetailsRequest
  ): Promise<ITableServerResponseFilter | undefined> {
    const rejectCode =
      request.rejectCode && request.rejectCode !== 'All' ? request.rejectCode : 'RJCT';
    const respondentRtn = request.respondentRtn || '021000021';

    const allRows: IRejectAnalysisTransactionDetail[] = [
      {
        messageId: `${respondentRtn}${rejectCode}000001`,
        amount: 125.5,
        processingDateTime: '2026-08-25T10:30:00Z',
        transactionType: 'Credit',
        source: '',
      },
      {
        messageId: `${respondentRtn}${rejectCode}000002`,
        amount: 1040.12,
        processingDateTime: '2026-08-25T11:45:00Z',
        transactionType: 'Credit',
        source: 'R',
      },
      {
        messageId: `${respondentRtn}${rejectCode}000003`,
        amount: 85,
        processingDateTime: '2026-08-25T13:05:00Z',
        transactionType: 'Credit',
        source: 'R',
      },
    ];

    if (request.sortBy) {
      const sortField = request.sortBy.charAt(0).toLowerCase() + request.sortBy.slice(1);
      allRows.sort((a: any, b: any) => {
        const av = a?.[sortField];
        const bv = b?.[sortField];

        if (av == null && bv == null) {
          return 0;
        }
        if (av == null) {
          return request.sortAsc ? -1 : 1;
        }
        if (bv == null) {
          return request.sortAsc ? 1 : -1;
        }

        if (typeof av === 'number' && typeof bv === 'number') {
          return request.sortAsc ? av - bv : bv - av;
        }

        const aDate = Date.parse(String(av));
        const bDate = Date.parse(String(bv));
        if (!Number.isNaN(aDate) && !Number.isNaN(bDate)) {
          return request.sortAsc ? aDate - bDate : bDate - aDate;
        }

        const cmp = String(av).localeCompare(String(bv), undefined, { numeric: true });
        return request.sortAsc ? cmp : -cmp;
      });
    }

    const pageSize = Number(request.pageSize ?? 10) || 10;
    const currentPage = Number(request.currentPage ?? 1) || 1;
    const first = (currentPage - 1) * pageSize;

    return {
      currentPage,
      pageCount: Math.ceil(allRows.length / pageSize),
      pageSize,
      rowCount: allRows.length,
      queryable: allRows.slice(first, first + pageSize),
    };
  }

  getById(messageId: string, type: string, _primaryRtn: string): Promise<any> {
    return this.transactionService.getById(messageId, type);
  }

  getReturnById(messageId: string, type: string, _primaryRtn: string): Promise<any> {
    return this.transactionService.getReturnById(messageId, type);
  }

  async tableExportCSV(_filter: ITableServerFilter): Promise<Blob | undefined> {
    return undefined;
  }

  async tableExportExcel(_filter: ITableServerFilter): Promise<any | undefined> {
    return undefined;
  }
}
